import React, { useState } from 'react';
import './Countries.css';

import CA from 'country-flag-icons/react/3x2/CA';
import AU from 'country-flag-icons/react/3x2/AU';
import US from 'country-flag-icons/react/3x2/US';
import GB from 'country-flag-icons/react/3x2/GB';
import DE from 'country-flag-icons/react/3x2/DE';
import SG from 'country-flag-icons/react/3x2/SG';
import MY from 'country-flag-icons/react/3x2/MY';
import TH from 'country-flag-icons/react/3x2/TH';
import ID from 'country-flag-icons/react/3x2/ID';
import AE from 'country-flag-icons/react/3x2/AE';
import NZ from 'country-flag-icons/react/3x2/NZ';
import JP from 'country-flag-icons/react/3x2/JP';
import VN from 'country-flag-icons/react/3x2/VN';
import HK from 'country-flag-icons/react/3x2/HK';

const flagMap = { CA, AU, US, GB, DE, SG, MY, TH, ID, AE, NZ, JP, VN, HK };

const Flag = ({ code, className = '' }) => {
  const F = flagMap[code.toUpperCase()];
  return F ? <F className={`cc-flag-svg ${className}`} /> : null;
};

const countries = [
  {
    name: 'United Kingdom',
    code: 'GB',
    visaType: 'Standard Visitor Visa',
    timeline: '15–20 working days after appointment',
    requirements: [
      'Valid passport (6+ months validity, minimum 2 blank back-to-back pages)',
      'Filled application form with signature',
      'Covering letter addressed to the Visa Officer, British High Commission',
      'Personal bank statement – last 6 months (with bank stamp)',
      'Personal ITR – last 3 years',
      'If employed: appointment letter, salary slips (last 6 months), leave certificate from employer',
      'If self-employed / business: ownership proof (proprietorship/partnership deed/AOA), company bank statement (6 months, bank stamp & sign), company ITR (3 years)',
      'If retired: retirement proof, 6-month pension proof, bank statement (6 months, bank seal & sign, 3–4 lakh balance per pax), personal ITR (3 years)',
      'If sponsored: sponsorship letter, sponsor ITR (3 years), bank statement (6 months, 3–4 lakh balance), passport copy, occupation proof of sponsor',
      'For invitee: invitation letter, passport copy, visa copy, residence proof',
      'For student: leave sanction letter, ID card copy',
      'For business visit: invitation letter from UK company on letterhead with sign & stamp',
    ],
  },
  {
    name: 'Schengen (Europe)',
    code: 'DE',
    visaType: 'Schengen Tourist / Business Visa',
    timeline: '10–15 working days after appointment',
    requirements: [
      'Valid national passport',
      '1 passport-size photograph',
      'Completed and signed application form',
      'Covering letter',
      'Travel insurance',
      'Flight booking',
      'Hotel booking',
      'Last 3-month personal bank statements',
      'Personal ITR (ITR-V / Acknowledgement) – last 2 assessment years',
      'If employed: employment contract, last 3-month salary slips',
      'If self-employed: GST / business registration, proof of ownership',
      'Invitation letter from business partner (for business visa)',
    ],
  },
  {
    name: 'United States',
    code: 'US',
    visaType: 'B1/B2 Tourist Visa',
    timeline: '30–60 days (varies by consulate)',
    requirements: [
      'Original valid passport (6+ months validity, 1 blank back-to-back page) + old passport',
      'DS-160 visa form (completed online by our team – Performa to be filled by applicant)',
      'Covering letter addressed to the Visa Officer, Embassy of United States of America',
      'Personal bank statement – last 6 months (original, bank seal & sign, 3–4 lakh balance)',
      'Personal ITR – last 3 years',
      'If employed: appointment letter, salary slips (6 months), original company NOC',
      'If self-employed / business: cover letter on company letterhead (stamp & sign), ownership proof, company bank statement (6 months, bank seal & sign), company ITR (3 years), balance sheet',
      'From invitee: invitation letter, passport stamp copy, residence proof',
      'For business visit: invitation letter on company letterhead',
    ],
  },
  {
    name: 'Australia',
    code: 'AU',
    visaType: 'Visitor Visa (Subclass 600)',
    timeline: '30–40 working days after submission',
    requirements: [
      'Valid passport (6+ months validity from departure date) – full scanned copy, all pages, current + old passports',
      'Covering letter addressed to the Australian High Commission',
      'Personal bank statement – last 6 months (bank seal, signature, 2–3 lakh balance per pax)',
      'Personal ITR – last 3 years',
      'Aadhaar card copy',
      'Family form 54 (if applicable)',
      'Applicant mobile number and email ID',
      'If employed: appointment letter, salary slips (6 months), leave certificate from employer',
      'If self-employed / business: brief company profile, ownership proof (proprietorship/partnership deed/AOA), company bank statement (6 months, updated), company ITR (3 years), balance sheet',
      'For business visit: invitation letter from Australian company on letterhead with sign & stamp',
    ],
  },
  {
    name: 'New Zealand',
    code: 'NZ',
    visaType: 'Visitor Visa',
    timeline: '30–40 working days',
    requirements: [
      'Clear printer-scanned passport (bio + address page + used pages) – current & old, in 1 single PDF',
      '1 photograph (35×45 mm, white background, matt finish – JPEG only)',
      'Travel itinerary',
      'Cover letter to New Zealand High Commission (purpose of visit, travel dates, expense clause)',
      'Personal bank statement – last 6 months (updated till date, bank seal & signature from authorised person – PDF only)',
      'Personal ITR – last 3 years (acknowledgement page only)',
      'Duly filled Performa form',
      'Hotel confirmation (if confirmed)',
      'Flight ticket (if confirmed)',
      "Spouse's passport copy (PDF only)",
      'Marriage certificate',
      'PAN card, Aadhaar card',
      'If employed: company NOC (stamp & sign), salary slips – last 6 months (separate PDF), joining letter',
      'If self-employed: cover letter on company letterhead (stamp & sign), company bank statement – last 6 months (with bank seal & signature or bank balance certificate), ITR (3 years), company memo / partnership deed / establishment license / proprietorship proof (separate PDF)',
    ],
  },
  {
    name: 'Japan',
    code: 'JP',
    visaType: 'Tourist Visa',
    timeline: '10–15 working days after submission',
    requirements: [
      'Visa application form with signature (as per passport)',
      'Original valid passport (6+ months validity)',
      '2 fresh unused photographs (2×2 inches, white background, matt finish)',
      'Covering letter to the Visa Officer, Embassy of Japan (with stamp & sign as per passport) – mentioning duration, purpose & expenses clause',
      'Personal bank statement – last 3 months (original, bank seal & signature)',
      'Personal ITR – last 2 years',
      'Air ticket & hotel confirmation / vouchers',
      'Day-to-day tour itinerary',
      'Applicant contact number and email ID',
      'If employed: original company NOC (stamp & sign), salary slips – last 3 months',
      'If self-employed / owner: cover letter on company letterhead (stamp & sign), company memo, partnership deed, establishment license or proprietorship proof',
      'If student: leave letter / report card from school & ID card',
      'If retired: retirement letter & pension slips (last 3 months)',
      'For business visit: invitation letter, letter of guarantee, schedule of stay',
    ],
  },
  {
    name: 'Singapore',
    code: 'SG',
    visaType: 'Tourist Visa',
    timeline: '7–8 working days after submission',
    requirements: [
      'Original passport (minimum 6 months validity)',
      'Passport-size photograph (35×45 mm / 3.5×4.5 cm, plain white background, no shadows or patterns)',
      'Completed application form with signature',
      'Covering letter',
      'Flight booking',
      'If employed: salary slips (last 3 months), NOC from employer, personal ITR (last 3 years)',
      'If self-employed / business: ownership proof (proprietorship/partnership deed/AOA), company bank statement (last 6 months, updated), company ITR (last 3 years), balance sheet',
    ],
  },
  {
    name: 'UAE',
    code: 'AE',
    visaType: 'Tourist Visa',
    timeline: '4–6 working days after submission',
    requirements: [
      'Passport (minimum 6 months validity)',
      'External passport cover page',
      'Passport-size photograph',
      'PAN card',
      'Flight booking',
      'Hotel booking',
    ],
  },
  {
    name: 'Thailand',
    code: 'TH',
    visaType: 'Tourist Visa / Digital Arrival Card',
    timeline: '3–7 days (visa) / 1 working day (DAC)',
    requirements: [
      'Passport (minimum 6 months validity)',
      'Flight booking',
      'Hotel booking',
      'Contact number & email ID',
      'Occupation details',
      'Thailand Digital Arrival Card (TDAC) – mandatory for all arrivals',
    ],
  },
  {
    name: 'Malaysia',
    code: 'MY',
    visaType: 'eVisa / Digital Arrival Card (MDAC)',
    timeline: '2–5 days',
    requirements: [
      'Valid passport (minimum 6 months validity)',
      'Return flight tickets',
      'Hotel confirmation',
      'Financial proof',
      'Malaysia Digital Arrival Card (MDAC) – mandatory for all visitors',
    ],
  },
  {
    name: 'Indonesia',
    code: 'ID',
    visaType: 'Visa on Arrival / eVisa',
    timeline: '2 working days after submission',
    requirements: [
      'Scanned passport bio & address page (JPG only)',
      'HD photo – white background, 35×45 mm (JPG only)',
      'Air ticket (PDF only)',
      'Hotel booking (PDF only)',
    ],
  },
  {
    name: 'Vietnam',
    code: 'VN',
    visaType: 'eVisa',
    timeline: '4–6 working days after submission',
    requirements: [
      'Scanned passport bio & address page (JPG only)',
      'HD photo – white background, 35×45 mm (JPG only)',
      'Air ticket (PDF only)',
      'Hotel booking (PDF only)',
    ],
  },
  {
    name: 'Hong Kong',
    code: 'HK',
    visaType: 'Pre-Arrival Registration (PAR)',
    timeline: '1 working day after submission',
    requirements: [
      'Passport (minimum 6 months validity)',
      'Flight booking',
      'Contact number',
    ],
  },
  {
    name: 'Canada',
    code: 'CA',
    visaType: 'Tourist Visa / eTA',
    timeline: '15–30 days',
    requirements: [
      'Valid passport (6+ months validity)',
      'Proof of funds (bank statements)',
      'Travel itinerary',
      'Biometrics (if required)',
      'Travel history documents',
      'Purpose of visit letter',
    ],
  },
];

export default function Countries() {
  const [sel, setSel] = useState(null);

  const open  = (c) => { setSel(c); document.body.style.overflow = 'hidden'; };
  const close = ()  => { setSel(null); document.body.style.overflow = ''; };
  const goInquiry = () => {
    close();
    setTimeout(() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }), 200);
  };

  return (
    <section id="countries" className="sec sec-dark">
      <div className="container">
        <div style={{ marginBottom: 36 }}>
          <p className="label"><i className="fas fa-earth-asia"></i> Countries We Serve</p>
          <h2 className="sec-title">Visa Assistance for <span>50+ Countries</span></h2>
          <div className="rule"></div>
          <p className="sec-body">Click any country to view visa type, processing timeline, and document requirements.</p>
        </div>

        <div className="countries-grid">
          {countries.map((c, i) => (
            <div className="country-card" key={i} onClick={() => open(c)}>
              <div className="cc-flag-wrap">
                <Flag code={c.code} />
              </div>
              <p className="cc-name">{c.name}</p>
              <p className="cc-type">{c.visaType}</p>
              <span className="cc-arrow">View details <i className="fas fa-arrow-right"></i></span>
            </div>
          ))}
        </div>

        <div className="countries-more">
          <i className="fas fa-plus-circle"></i>
          And many more countries — contact us for any destination not listed above
        </div>
      </div>

      {sel && (
        <div className="modal-overlay" onClick={close}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={close} aria-label="Close">
              <i className="fas fa-xmark"></i>
            </button>

            <div className="modal-head">
              <div className="modal-flag-wrap">
                <Flag code={sel.code} />
              </div>
              <div className="modal-head-text">
                <h3>{sel.name}</h3>
                <p><i className="fas fa-passport"></i> {sel.visaType}</p>
              </div>
            </div>

            <div className="modal-body">
              <div className="modal-row">
                <p className="modal-row-label"><i className="fas fa-clock"></i> Processing Timeline</p>
                <div className="modal-timeline-val">
                  <i className="fas fa-hourglass-half"></i> {sel.timeline}
                </div>
              </div>

              <div className="modal-row">
                <p className="modal-row-label"><i className="fas fa-list-check"></i> Document Requirements</p>
                <div className="modal-reqs">
                  {sel.requirements.map((r, j) => (
                    <div className="modal-req" key={j}>
                      <i className="fas fa-check"></i> {r}
                    </div>
                  ))}
                </div>
              </div>

              <div className="modal-note">
                <i className="fas fa-circle-info"></i>
                <span>Requirements vary by individual profile. Contact us for a personalised checklist based on your travel history and occupation.</span>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-teal" onClick={goInquiry}>
                <i className="fas fa-paper-plane"></i>
                Apply for {sel.name} Visa
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
